import { Injectable, inject, DestroyRef, Injector } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, DetachedRouteHandle } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouteHandleEntry, RouteLifecycleEvent } from '@core/navigation-state/models/route-handle-entry.model';

const MAX_HANDLES = 3;
const DEFAULT_TTL_MS = 120_000; // 2 minutes

// Single source of truth for what a navigation means to the reuse strategy.
// Replaces the old preserveOriginSignal + returningFromRedirectSignal pair,
// which could desync when a handle was evicted before a signal was consumed.
type NavigationMode = 'idle' | 'going-to-dashboard' | 'returning-from-dashboard' | 'going-to-detail' | 'returning-from-detail';

// Detail sub-routes that trigger state preservation of their parent page.
// When navigating TO any of these, the origin page is cached.
// When navigating AWAY from these back to the origin, it is reattached.
const DETAIL_SUB_ROUTES = [
  '/fullfillment/shipment-detail',
  '/awaiting-shipment/shipment-detail',
];

@Injectable({ providedIn: 'root' })
export class NavigationStateService {

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector   = inject(Injector);

  // ── LRU handle store ────────────────────────────────────────────────────────
  private readonly handles = new Map<string, RouteHandleEntry>();

  // ── Navigation mode ──────────────────────────────────────────────────────────
  // Set on every NavigationStart; read synchronously by PageReuseStrategy
  // during the same navigation cycle — no race conditions, no stale flags.
  private navigationMode: NavigationMode = 'idle';

  // ── Origin tracking ──────────────────────────────────────────────────────────
  // Remembers which page was last detached so we can apply selective eviction
  // when the user navigates to the dashboard from a DIFFERENT page later.
  private lastCachedUrl: string | null = null;

  // ── Reattach flag ────────────────────────────────────────────────────────────
  // Set in emitReattached(), cleared on next NavigationStart.
  // NavigationInterceptorService reads this to skip snapshot restore when
  // the live component tree was already reattached (it already has perfect state).
  private wasReattachedFlag = false;

  // ── Lifecycle event stream ───────────────────────────────────────────────────
  private readonly lifecycleSubject = new Subject<RouteLifecycleEvent>();
  readonly routeLifecycle$: Observable<RouteLifecycleEvent> = this.lifecycleSubject.asObservable();

  // ── Tab state ────────────────────────────────────────────────────────────────
  private tabIndices = { activeTabIndex: 0, activeNestedTabIndex: 0 };

  constructor() {
    // Defer router access to break the circular dependency:
    // Router → RouteReuseStrategy → NavigationStateService → Router
    Promise.resolve().then(() => this.listenToRouterEvents());
  }

  // ── Public API: LRU store ────────────────────────────────────────────────────

  storeHandle(url: string, handle: DetachedRouteHandle): void {
    if (this.handles.size >= MAX_HANDLES) {
      const oldestKey = this.handles.keys().next().value;
      if (oldestKey) {
        console.log(`[NavState] LRU cap reached — evicting oldest: "${oldestKey}"`);
        this.destroyHandle(this.handles.get(oldestKey)!);
        this.handles.delete(oldestKey);
      }
    }
    this.handles.set(url, { url, handle, ts: Date.now(), ttl: DEFAULT_TTL_MS, retrieveCount: 0, reattached: false });
    console.log(`[NavState] Stored handle for "${url}" (total: ${this.handles.size})`);
  }

  hasValidHandle(url: string): boolean {
    const entry = this.handles.get(url);
    if (!entry) return false;

    // NOTE: do NOT gate on entry.reattached here.
    // Angular calls shouldAttach → retrieve TWICE per back-navigation:
    //   1. Planning phase (createRouterState) — builds the future state tree
    //   2. Activation phase (RouterOutlet.activateWith) — actually mounts to the DOM
    // The entry is only deleted from the map after the 2nd retrieve (activation phase).
    // Blocking on reattached here would make the 2nd shouldAttach return false,
    // causing Angular to create a fresh component instead of using the stored handle.

    if (Date.now() - entry.ts >= entry.ttl) {
      console.log(`[NavState] Handle for "${url}" expired — evicting`);
      this.destroyHandle(entry);
      this.handles.delete(url);
      return false;
    }

    if (!this.isHandleAlive(entry.handle)) {
      console.log(`[NavState] Handle for "${url}" is destroyed — evicting`);
      // Do NOT call destroy() here — Angular/HMR may still hold a reference
      this.handles.delete(url);
      return false;
    }

    return true;
  }

  retrieveHandle(url: string): DetachedRouteHandle | null {
    const entry = this.handles.get(url);
    if (!entry) return null;

    if (Date.now() - entry.ts >= entry.ttl) {
      this.destroyHandle(entry);
      this.handles.delete(url);
      return null;
    }

    if (!this.isHandleAlive(entry.handle)) {
      this.handles.delete(url);
      return null;
    }

    // Angular calls retrieve() TWICE per back-navigation (planning + activation).
    // Keep the entry in the map after the 1st call so the 2nd shouldAttach also
    // returns true. Only remove it after the 2nd retrieve (activation phase) —
    // at that point Angular owns the component and we must NOT call destroy().
    entry.retrieveCount++;
    if (entry.retrieveCount >= 2) {
      entry.reattached = true;      // guard destroyHandle from killing a live component
      this.handles.delete(url);     // Angular now owns it — remove from our map
      console.log(`[NavState] Handle for "${url}" activated (phase 2) — removed from map`);
    }
    return entry.handle;
  }

  evictHandle(url: string): void {
    const entry = this.handles.get(url);
    if (!entry) return;
    this.destroyHandle(entry);
    this.handles.delete(url);
    console.log(`[NavState] Evicted handle for "${url}"`);
  }

  clearAllHandles(): void {
    for (const entry of this.handles.values()) {
      this.destroyHandle(entry);
    }
    this.handles.clear();
    console.log('[NavState] All handles cleared');
  }

  // ── Public API: Navigation mode ──────────────────────────────────────────────

  isGoingToDashboard(): boolean {
    return this.navigationMode === 'going-to-dashboard' || this.navigationMode === 'going-to-detail';
  }

  isReturningFromDashboard(): boolean {
    return this.navigationMode === 'returning-from-dashboard' || this.navigationMode === 'returning-from-detail';
  }

  // ── Public API: Reattach flag ────────────────────────────────────────────────

  emitDetached(url: string): void {
    this.lifecycleSubject.next({ event: 'detached', url });
  }

  emitReattached(url: string): void {
    this.wasReattachedFlag = true;
    this.lifecycleSubject.next({ event: 'reattached', url });
  }

  wasLastNavigationAReattach(): boolean {
    return this.wasReattachedFlag;
  }

  // ── Public API: Tab state ────────────────────────────────────────────────────

  saveTabIndices(activeTabIndex: number, activeNestedTabIndex: number): void {
    this.tabIndices = { activeTabIndex, activeNestedTabIndex };
  }

  getTabIndices(): { activeTabIndex: number; activeNestedTabIndex: number } {
    return { ...this.tabIndices };
  }

  // ── Backwards-compat stubs ───────────────────────────────────────────────────
  // customer-dashboard-layout calls setReturnSignal() before location.back().
  // Navigation mode is now detected automatically from router events — keeping
  // this method avoids a breaking change in that component.
  setReturnSignal(): void { /* no-op — mode is driven by router events */ }

  // ── Private: Router event listener ──────────────────────────────────────────

  private listenToRouterEvents(): void {
    const router = this.injector.get(Router);
    router.events.pipe(
      filter(e => e instanceof NavigationStart || e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.onNavigationStart(event.url, router.url);
      }
      // NavigationEnd: mode intentionally kept so NavigationInterceptorService
      // can still read isReturningFromDashboard() in its own NavigationEnd handler
      // within the same cycle. Overwritten on the NEXT NavigationStart.
    });
  }

  private onNavigationStart(destinationUrl: string, currentUrl: string): void {
    // Clear reattach flag from the previous navigation cycle
    this.wasReattachedFlag = false;

    const destPath = destinationUrl.split('?')[0];
    const currPath = currentUrl.split('?')[0];

    const isGoingToDetail = DETAIL_SUB_ROUTES.some(r => destPath.startsWith(r));
    const isComingFromDetail = DETAIL_SUB_ROUTES.some(r => currPath.startsWith(r));

    if (destPath.includes('/customers/dashboard') || isGoingToDetail) {
      this.navigationMode = isGoingToDetail ? 'going-to-detail' : 'going-to-dashboard';

      // ── Selective eviction ────────────────────────────────────────────────
      // We only ever cache ONE origin page at a time. Before storing the new
      // handle, clean up whatever was cached previously:
      //
      // Case A — Different origin: user went /fulfillment → dashboard → /review
      //   → dashboard. The /fulfillment handle is now stale; evict it so only
      //   /review is cached.
      //
      // Case B — Same origin going to dashboard AGAIN (e.g. /fulfillment → back
      //   → /fulfillment → dashboard): the previous handle for /fulfillment may
      //   still be in the map (not consumed by reattach). Evict it so a fresh
      //   handle is stored below via shouldDetach/store.
      if (this.lastCachedUrl && this.lastCachedUrl !== currPath) {
        console.log(`[NavState] Origin changed "${this.lastCachedUrl}" → "${currPath}" — evicting stale`);
        this.evictHandle(this.lastCachedUrl);
      } else if (this.handles.has(currPath)) {
        const existing = this.handles.get(currPath)!;
        if (!existing.reattached) {
          // Going to dashboard again before ever returning — stale unused handle, replace it.
          console.log(`[NavState] Same origin "${currPath}" re-entering dashboard — replacing unused handle`);
          this.evictHandle(currPath);
        } else {
          // Previously reattached — safe to drop the map entry so a fresh handle is stored.
          console.log(`[NavState] Same origin "${currPath}" — clearing reattached entry for fresh handle`);
          this.handles.delete(currPath);
        }
      }

      this.lastCachedUrl = currPath;
      console.log(`[NavState] Mode: going-to-dashboard (origin: "${currPath}")`);

    } else if (currPath.includes('/customers/dashboard') || isComingFromDetail) {
      // Leaving the dashboard or detail page — enable reattach so the user's cached page is
      // restored. The mode is read by PageReuseStrategy.shouldAttach().
      this.navigationMode = isComingFromDetail ? 'returning-from-detail' : 'returning-from-dashboard';
      console.log(`[NavState] Mode: ${this.navigationMode} → "${destPath}"`);

    } else {
      this.navigationMode = 'idle';
      // Direct navigation to a cached URL via sidebar/bookmark — evict the
      // frozen handle so the page loads fresh (not from a stale detached tree).
      if (this.handles.has(destPath)) {
        console.log(`[NavState] Direct nav to "${destPath}" — evicting frozen handle`);
        this.evictHandle(destPath);
      }
    }
  }

  // ── Private: Handle validation ───────────────────────────────────────────────

  private isHandleAlive(handle: DetachedRouteHandle): boolean {
    try {
      const componentRef = (handle as any)?.componentRef;
      if (!componentRef) return false;
      // hostView.destroyed catches views Angular destroyed internally (CD errors, HMR).
      // Guard: hostView may be null on some lazy-load paths — only check if present.
      if (componentRef.hostView != null && componentRef.hostView.destroyed === true) return false;
      const componentType = componentRef.componentType;
      // ɵcmp is nulled by HMR in dev mode when a file is saved — guard against crash.
      return componentType != null && (componentType as any).ɵcmp != null;
    } catch {
      return false;
    }
  }

  private destroyHandle(entry: RouteHandleEntry): void {
    // Never destroy if Angular has already retrieved the handle (planning or activation phase).
    // retrieveCount >= 1 means Angular holds a reference to this component tree.
    if (entry.reattached || entry.retrieveCount >= 1) return;
    try {
      (entry.handle as any)?.componentRef?.destroy();
    } catch {
      // Ignore — view may already be destroyed
    }
  }
}
